/**
 * ⭐⭐ THE PRODUCT VERSION — FIVE LEVELS (owner, 2026-10-04).
 *
 *   Main . Major . Minor . Subminor . Trivial      e.g. 0.7.0.0.0
 *
 * Bump ONE level per release; every level below it resets to 0. Changes no user can
 * see (tests, docs, refactors, tooling) bump NOTHING — the build id (the commit,
 * shown on hover) already changes on every deploy.
 *
 * Source of truth: `version.json`. Bump with `npm run release -- <level> "<note>"`,
 * which also writes `docs/RELEASES.md` and keeps `package.json` and the iOS app in step.
 *
 * ⚠️ npm and the iOS App Store accept only THREE numbers, so `package.json` and the iOS
 * `MARKETING_VERSION` carry `Main.Major.Minor` (`semverOf`). Subminor and Trivial live
 * only here.
 */

export const LEVELS = ['main', 'major', 'minor', 'subminor', 'trivial'] as const;
export type Level = (typeof LEVELS)[number];

/** When each level moves — the rule a PR's `Release:` line is judged against. */
export const LEVEL_RULES: Record<Level, string> = {
  main: 'An era: 0 = beta, 1 = public launch. The BETA label shows while Main is 0.',
  major: 'A headline release — a new pillar or a reworked experience (e.g. unveiling Growth Profiles, the Observatory UI, the mobile companion).',
  minor: 'A new feature, or a change in how an existing feature works.',
  subminor: 'Improvements and fixes to existing features that users would notice.',
  trivial: 'Tiny user-visible changes — copy, a colour, spacing, a typo.',
};

export type Version = readonly [number, number, number, number, number];

const PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

/** Parses exactly five non-negative integers, no leading zeros; anything else → null. */
export function parseVersion(text: string): Version | null {
  const m = PATTERN.exec(text.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5])] : null;
}

export const formatVersion = (v: Version): string => v.join('.');

export const isLevel = (s: string): s is Level => (LEVELS as readonly string[]).includes(s);

/** Raises `level` by one and resets every level below it. */
export function bumpVersion(v: Version, level: Level): Version {
  const i = LEVELS.indexOf(level);
  return v.map((n, j) => (j < i ? n : j === i ? n + 1 : 0)) as unknown as Version;
}

/** The three-number form npm and the App Store accept: Main.Major.Minor. */
export const semverOf = (v: Version): string => `${v[0]}.${v[1]}.${v[2]}`;

/* ⚠️ The iOS app's version shown in the App Store (`MARKETING_VERSION`, one per build
   configuration in project.pbxproj). It said 1.0 while the product was 0.7.x because
   nothing kept it in step (owner, 2026-10-04). `CURRENT_PROJECT_VERSION` — the build
   number — is NOT this: it must rise on every App Store upload and is left to Xcode. */
const MARKETING_VERSION = /MARKETING_VERSION = ([^;]+);/g;

/** Every `MARKETING_VERSION` in an Xcode project file. */
export const marketingVersions = (pbxproj: string): string[] =>
  [...pbxproj.matchAll(MARKETING_VERSION)].map(m => m[1].trim());

/** The project file with every `MARKETING_VERSION` set to `semver`. */
export const setMarketingVersion = (pbxproj: string, semver: string): string =>
  pbxproj.replace(MARKETING_VERSION, `MARKETING_VERSION = ${semver};`);

/** Beta until the first public launch (Main ≥ 1). */
export const isBeta = (v: Version): boolean => v[0] < 1;

/** A `docs/RELEASES.md` entry, newest first. */
export function releaseEntry(v: Version, level: Level, date: string, note: string): string {
  return `## ${formatVersion(v)} — ${date} · ${level}\n\n- ${note.trim()}\n`;
}

/** The version of the newest entry in `docs/RELEASES.md`, or null. */
export function newestReleaseVersion(markdown: string): string | null {
  return /^## (\S+) — /m.exec(markdown)?.[1] ?? null;
}
